<template>
  <div>
    <Head :title="__('Activity Log')" />

    <div class="bg-white rounded-xl shadow-sm border border-gray-100">
      <div class="login-log-section-header px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
        <h2 class="font-semibold text-gray-800">{{ __('Activity Log') }}</h2>
        <div class="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            @click="refresh"
            :title="__('Refresh')"
            class="al-refresh-btn flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border transition-colors border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
              stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4" :class="{ 'animate-spin': refreshing }">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            {{ __('Refresh') }}
          </button>
          <select v-model="form.status" class="form-select text-sm w-32">
            <option :value="null">{{ __('All status') }}</option>
            <option value="ok">{{ __('OK (2xx/3xx)') }}</option>
            <option value="error">{{ __('Error (>=400)') }}</option>
          </select>
          <select v-model="form.method" class="form-select text-sm w-28">
            <option :value="null">{{ __('All methods') }}</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
            <option value="GET">GET</option>
          </select>
          <search-input v-model="form.search" class="w-64" @reset="reset" />
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap ticket_list">
          <tr class="text-left font-bold">
            <th>{{ __('User') }}</th>
            <th>{{ __('Status') }}</th>
            <th>{{ __('Action') }}</th>
            <th>{{ __('Method') }}</th>
            <th>{{ __('Path') }}</th>
            <th>{{ __('Time') }}</th>
            <th>{{ __('IP') }}</th>
          </tr>
          <tr v-for="log in logs.data" :key="log.id" class="hover:bg-gray-100 focus-within:bg-gray-100 ticket-row">
            <td class="border-t">
              <div class="px-4 py-2 text-sm font-medium">{{ log.user_name }}</div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2">
                <span class="px-2 py-0.5 rounded-full text-xs font-semibold font-mono" :class="statusClass(log.status_code)">
                  {{ log.status_code }}
                </span>
              </div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2 text-sm capitalize">{{ log.action }}</div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2 text-xs font-mono opacity-70">{{ log.method }}</div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2 text-xs font-mono opacity-60">/{{ log.path }}</div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2 text-sm opacity-80">{{ log.created_at }}</div>
            </td>
            <td class="border-t">
              <div class="px-4 py-2 text-xs font-mono opacity-50">{{ log.ip_address || '—' }}</div>
            </td>
          </tr>
          <tr v-if="logs.data.length === 0">
            <td class="login-log-empty px-4 py-4 border-t text-sm" colspan="7">{{ __('No activity records found.') }}</td>
          </tr>
        </table>
      </div>
    </div>

    <pagination class="mt-6" :links="logs.links" />
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'

export default {
  components: {
    Head,
    Pagination,
    SearchInput,
  },
  layout: Layout,
  props: {
    filters: Object,
    logs: Object,
  },
  data() {
    return {
      refreshing: false,
      form: {
        search: this.filters.search,
        method: this.filters.method,
        status: this.filters.status,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('activity_log'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    refresh() {
      this.refreshing = true
      this.$inertia.reload({
        preserveScroll: true,
        onFinish: () => { this.refreshing = false },
      })
    },
    statusClass(code) {
      if (code >= 500) return 'bg-red-100 text-red-700'
      if (code >= 400) return 'bg-amber-100 text-amber-700'
      // 2xx & 3xx sama-sama sukses → hijau
      return 'bg-green-100 text-green-700'
    },
  },
}
</script>

<style scoped>
/* Dashboard menyalakan dark mode lewat class .dark (bukan [data-mode], jadi varian dark: Tailwind tidak berlaku di sini). */
.dark .al-refresh-btn {
  border-color: #4b5563;   /* gray-600 */
  color: #e5e7eb;          /* gray-200 */
}
.dark .al-refresh-btn:hover {
  background-color: #374151; /* gray-700 */
}
</style>
